import { useState } from 'react'

export default function ProviderConfigForm({ config, onSubmit, onCancel, loading }) {
  const [formData, setFormData] = useState({
    ProviderName: config?.ProviderName || '',
    ConfigDetails: config?.ConfigDetails ? JSON.stringify(config.ConfigDetails, null, 2) : '',
    IsActive: config?.IsActive ?? true,
    IsDefault: config?.IsDefault ?? false
  })
  const [errors, setErrors] = useState({})

  const validate = () => {
    const newErrors = {}
    if (!formData.ProviderName.trim()) newErrors.ProviderName = 'Provider name is required'
    
    if (formData.ConfigDetails.trim()) {
      try {
        JSON.parse(formData.ConfigDetails)
      } catch (e) {
        newErrors.ConfigDetails = 'Must be valid JSON'
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    const submitData = {
      ProviderName: formData.ProviderName.trim(),
      ConfigDetails: formData.ConfigDetails.trim() ? JSON.parse(formData.ConfigDetails) : null,
      IsActive: formData.IsActive,
      IsDefault: formData.IsDefault
    }

    const result = await onSubmit(submitData)
    if (result?.error) {
      setErrors({ submit: result.error.message })
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Provider Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Provider Name *
        </label>
        <select
          value={formData.ProviderName}
          onChange={(e) => handleChange('ProviderName', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">Select a provider...</option>
          <option value="Firebase">Firebase FCM</option>
          <option value="APNS">Apple Push Notification Service</option>
          <option value="OneSignal">OneSignal</option>
        </select>
        {errors.ProviderName && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.ProviderName}</p>}
      </div>

      {/* Config Details */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Configuration Details (JSON)
        </label>
        <textarea
          value={formData.ConfigDetails}
          onChange={(e) => handleChange('ConfigDetails', e.target.value)}
          rows={10}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
          placeholder={'{\n  "project_id": "your-project-id",\n  "private_key": "-----BEGIN PRIVATE KEY-----\\n...\\n-----END PRIVATE KEY-----\\n",\n  "client_email": "firebase-adminsdk@your-project.iam.gserviceaccount.com"\n}'}
        />
        {errors.ConfigDetails && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.ConfigDetails}</p>}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Enter provider-specific configuration (e.g., Firebase service account JSON)
        </p>
      </div>

      {/* Is Active */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          checked={formData.IsActive}
          onChange={(e) => handleChange('IsActive', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          Active (enable this provider)
        </label>
      </div>

      {/* Is Default */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isDefault"
          checked={formData.IsDefault}
          onChange={(e) => handleChange('IsDefault', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          Default provider for this account
        </label>
      </div>

      {errors.submit && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{errors.submit}</p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition disabled:opacity-50 flex items-center space-x-2"
        >
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          <span>{config ? 'Update' : 'Add'} Provider</span>
        </button>
      </div>
    </form>
  )
}
